import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabaseHelpers } from '../config/supabase';
import Toast from './Toast';
import ConfirmModal from './ConfirmModal';
import { useToast } from '../utils/useToast';
import './PublicProfile.css';

const PublicProfile = ({ currentUser, supabaseIntegration }) => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { toast, showToast } = useToast();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [friendshipStatus, setFriendshipStatus] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    bio: '',
    display_name: ''
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [showRemoveFriendModal, setShowRemoveFriendModal] = useState(false);
  const [onboardingResponses, setOnboardingResponses] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(false);

  const isOwnProfile = currentUser && currentUser.sub === userId;

  useEffect(() => {
    if (userId) {
      loadProfileData();
      loadOnboardingResponses();
      if (!isOwnProfile && currentUser) {
        loadFriendshipStatus();
      }
    }
  }, [userId, currentUser]);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: profileError } = await supabaseHelpers.getProfile(userId);
      
      if (profileError) {
        setError('Profile not found');
        return;
      }
      
      setProfileData(data);
      setEditForm({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        bio: data.bio || '',
        display_name: data.display_name || ''
      });
    } catch (err) {
      setError('Failed to load profile');
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFriendshipStatus = async () => {
    try {
      const { data } = await supabaseIntegration.getFriendshipStatus(userId);
      setFriendshipStatus(data);
    } catch (err) {
      console.error('Error loading friendship status:', err);
    }
  };

  const loadOnboardingResponses = async () => {
    try {
      setLoadingResponses(true);
      console.log('🎯 Loading onboarding responses for user:', userId);
      
      // Use the getUserProfileResponses function from supabaseHelpers
      const { data, error } = await supabaseIntegration.supabaseHelpers.getUserProfileResponses(userId);
      
      if (error) {
        console.error('Error loading onboarding responses:', error);
        return;
      }
      
      // Filter to only onboarding category responses and sort by display order
      const onboardingData = (data || [])
        .filter(response => response.question_category === 'onboarding')
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      
      console.log('🎯 Loaded onboarding responses:', onboardingData);
      setOnboardingResponses(onboardingData);
    } catch (err) {
      console.error('Error loading onboarding responses:', err);
    } finally {
      setLoadingResponses(false);
    }
  };

  const handleSendFriendRequest = async () => {
    if (!currentUser) return;
    
    try {
      setActionLoading(true);
      const { data, error } = await supabaseIntegration.sendFriendRequest(userId);
      
      if (error) {
        const errorMessage = typeof error === 'string' ? error : 
                           error?.message || 
                           error?.error_description || 
                           'Unknown error occurred';
        console.error('Send friend request error:', error);
        showToast(`Failed to send friend request: ${errorMessage}`, 'error');
        return;
      }
      
      // Update friendship status to show request sent
      setFriendshipStatus({ status: 'pending', requester_id: currentUser.sub });
      showToast('✅ Friend request sent successfully!', 'success');
    } catch (err) {
      console.error('Error sending friend request:', err);
      const errorMessage = err?.message || 'An unexpected error occurred';
      showToast(`Failed to send friend request: ${errorMessage}`, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAcceptFriendRequest = async () => {
    if (!currentUser || !friendshipStatus) return;
    
    try {
      setActionLoading(true);
      const { error } = await supabaseIntegration.acceptFriendRequest(friendshipStatus.requester_id);
      
      if (error) {
        const errorMessage = typeof error === 'string' ? error : 
                           error?.message || 
                           error?.error_description || 
                           'Unknown error occurred';
        console.error('Accept friend request error:', error);
        showToast(`Failed to accept friend request: ${errorMessage}`, 'error');
        return;
      }
      
      // Update friendship status to show accepted
      setFriendshipStatus({ status: 'accepted' });
      showToast('✅ Friend request accepted! You can now coordinate together.', 'success');
    } catch (err) {
      console.error('Error accepting friend request:', err);
      const errorMessage = err?.message || 'An unexpected error occurred';
      showToast(`Failed to accept friend request: ${errorMessage}`, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectFriendRequest = async () => {
    if (!currentUser || !friendshipStatus) return;
    
    try {
      setActionLoading(true);
      const { error } = await supabaseIntegration.rejectFriendRequest(friendshipStatus.requester_id);
      
      if (error) {
        const errorMessage = typeof error === 'string' ? error : 
                           error?.message || 
                           error?.error_description || 
                           'Unknown error occurred';
        console.error('Reject friend request error:', error);
        showToast(`Failed to reject friend request: ${errorMessage}`, 'error');
        return;
      }
      
      // Update friendship status to show no relationship
      setFriendshipStatus({ status: 'none' });
      showToast('Friend request declined', 'info');
    } catch (err) {
      console.error('Error rejecting friend request:', err);
      const errorMessage = err?.message || 'An unexpected error occurred';
      showToast(`Failed to reject friend request: ${errorMessage}`, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveFriend = async () => {
    if (!currentUser) return;
    
    try {
      setActionLoading(true);
      const { error } = await supabaseIntegration.removeFriend(userId);
      
      if (error) {
        const errorMessage = typeof error === 'string' ? error : 
                           error?.message || 
                           error?.error_description || 
                           'Unknown error occurred';
        console.error('Remove friend error:', error);
        showToast(`Failed to remove friend: ${errorMessage}`, 'error');
        return;
      }
      
      // Update friendship status to show no relationship
      setFriendshipStatus({ status: 'none' });
      setShowRemoveFriendModal(false);
      showToast('Friend removed', 'info');
    } catch (err) {
      console.error('Error removing friend:', err);
      const errorMessage = err?.message || 'An unexpected error occurred';
      showToast(`Failed to remove friend: ${errorMessage}`, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!currentUser) return;
    
    try {
      setActionLoading(true);
      const { data, error } = await supabaseIntegration.updateUserProfile(editForm);
      
      if (error) {
        showToast(`Failed to update profile: ${error}`, 'error');
        return;
      }
      
      setProfileData(data);
      setIsEditing(false);
      showToast('✅ Profile updated successfully!', 'success');
    } catch (err) {
      console.error('Error updating profile:', err);
      showToast('Failed to update profile', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditForm({
      first_name: profileData.first_name || '',
      last_name: profileData.last_name || '',
      bio: profileData.bio || '',
      display_name: profileData.display_name || ''
    });
    setIsEditing(false);
  };

  const handleCopyProfileLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast('✅ Profile link copied to clipboard!', 'success');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showToast('✅ Profile link copied to clipboard!', 'success');
    }
  };

  const renderFriendshipButton = () => {
    if (!currentUser || isOwnProfile) return null;
    
    if (!friendshipStatus || friendshipStatus.status === 'none') {
      return (
        <button 
          className="friend-action-btn add-friend"
          onClick={handleSendFriendRequest}
          disabled={actionLoading}
        >
          {actionLoading ? '⏳' : '👋'} Add Friend
        </button>
      );
    }
    
    if (friendshipStatus.status === 'pending') {
      if (friendshipStatus.requester_id === currentUser.sub) {
        return (
          <button className="friend-action-btn pending" disabled>
            ⏳ Request Sent
          </button>
        );
      } else {
        return (
          <div className="friend-request-actions">
            <button 
              className="friend-action-btn accept"
              onClick={handleAcceptFriendRequest}
              disabled={actionLoading}
            >
              {actionLoading ? '⏳' : '✅'} Accept
            </button>
            <button 
              className="friend-action-btn reject"
              onClick={handleRejectFriendRequest}
              disabled={actionLoading}
            >
              {actionLoading ? '⏳' : '❌'} Decline
            </button>
          </div>
        );
      }
    }
    
    if (friendshipStatus.status === 'accepted') {
      return (
        <button 
          className="friend-action-btn remove-friend"
          onClick={() => setShowRemoveFriendModal(true)}
          disabled={actionLoading}
        >
          {actionLoading ? '⏳' : '🗑️'} Remove Friend
        </button>
      );
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="public-profile-container">
        <div className="profile-loading">
          <div className="loading-spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !profileData) {
    return (
      <div className="public-profile-container">
        <div className="profile-error">
          <div className="error-icon">❌</div>
          <h2>Profile Not Found</h2>
          <p>{error || 'This profile does not exist or has been removed.'}</p>
          <button onClick={() => navigate('/')} className="back-home-btn">
            🏠 Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="public-profile-container">
      {/* Toast Notification */}
      {toast && <Toast message={toast.message} type={toast.type} />}
      
      {/* Remove Friend Confirmation Modal */}
      <ConfirmModal
        isOpen={showRemoveFriendModal}
        onClose={() => setShowRemoveFriendModal(false)}
        onConfirm={handleRemoveFriend}
        title="Remove Friend"
        message={`Are you sure you want to remove ${profileData?.display_name || profileData?.full_name || 'this person'} from your friends list?`}
        confirmText="Remove Friend"
        cancelText="Cancel"
        confirmButtonClass="confirm-btn"
        isLoading={actionLoading}
      />
      
      <div className="profile-header">
        <button onClick={() => navigate('/')} className="back-btn">
          ← Back
        </button>
        <h1>User Profile</h1>
      </div>

      <div className="profile-card">
        <div className="profile-avatar-section">
          <img 
            src={profileData.avatar_url || '/default-avatar.png'} 
            alt={`${profileData.display_name || 'User'}'s avatar`}
            className="profile-avatar"
          />
          <div className="profile-status">
            {isOwnProfile && <span className="own-profile-badge">Your Profile</span>}
            {friendshipStatus?.status === 'accepted' && <span className="friend-badge">Friend</span>}
          </div>
        </div>

        <div className="profile-info">
          {isEditing ? (
            <div className="edit-form">
              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={editForm.display_name}
                  onChange={(e) => setEditForm({...editForm, display_name: e.target.value})}
                  placeholder="How you want to be known"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                    placeholder="First name"
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                    placeholder="Last name"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Bio</label>
                <textarea
                  value={editForm.bio}
                  onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                  placeholder="Tell others about yourself..."
                  rows="3"
                />
              </div>
              
              <div className="edit-actions">
                <button 
                  onClick={handleSaveProfile}
                  disabled={actionLoading}
                  className="save-btn"
                >
                  {actionLoading ? '⏳ Saving...' : '💾 Save Changes'}
                </button>
                <button 
                  onClick={handleCancelEdit}
                  disabled={actionLoading}
                  className="cancel-btn"
                >
                  ❌ Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="profile-display">
              <h2>{profileData.display_name || profileData.full_name || 'Anonymous User'}</h2>
              
              <div className="profile-details">
                {(profileData.first_name || profileData.last_name) && (
                  <p className="full-name">
                    <strong>Name:</strong> {[profileData.first_name, profileData.last_name].filter(Boolean).join(' ')}
                  </p>
                )}
                
                {profileData.username && (
                  <p className="username">
                    <strong>Username:</strong> @{profileData.username}
                  </p>
                )}
                
                {profileData.bio && (
                  <div className="bio">
                    <strong>About:</strong>
                    <p>{profileData.bio}</p>
                  </div>
                )}
                
                <div className="profile-meta">
                  <p><strong>Member since:</strong> {new Date(profileData.created_at).toLocaleDateString()}</p>
                  {profileData.last_login_at && (
                    <p><strong>Last active:</strong> {new Date(profileData.last_login_at).toLocaleDateString()}</p>
                  )}
                </div>

                {/* Onboarding Responses Section */}
                {onboardingResponses.length > 0 && (
                  <div className="onboarding-responses-section">
                    <h3>About {isOwnProfile ? 'You' : (profileData.display_name || profileData.full_name || 'This Person')}</h3>
                    <div className="onboarding-responses">
                      {onboardingResponses.map((response, index) => (
                        <div key={response.response_id || index} className="onboarding-response">
                          <div className="question-text">{response.question_text}</div>
                          <div className="answer-text">{response.user_answer}</div>
                          {response.answered_at && (
                            <div className="answer-date">
                              Answered on {new Date(response.answered_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Loading state for onboarding responses */}
                {loadingResponses && (
                  <div className="onboarding-responses-section">
                    <h3>About {isOwnProfile ? 'You' : (profileData.display_name || profileData.full_name || 'This Person')}</h3>
                    <div className="onboarding-loading">
                      <div className="loading-spinner"></div>
                      <span>Loading responses...</span>
                    </div>
                  </div>
                )}

                {/* Account Information Section - Only show for own profile */}
                {isOwnProfile && currentUser && (
                  <div className="account-info-section">
                    <h3>Account Information</h3>
                    <div className="account-details">
                      <div className="account-field">
                        <label>Full Name:</label>
                        <span>{currentUser.name}</span>
                      </div>
                      <div className="account-field">
                        <label>Email:</label>
                        <span>{currentUser.email}</span>
                      </div>
                      <div className="account-field">
                        <label>First Name:</label>
                        <span>{currentUser.given_name}</span>
                      </div>
                      <div className="account-field">
                        <label>Last Name:</label>
                        <span>{currentUser.family_name}</span>
                      </div>
                      <div className="account-field">
                        <label>Email Verified:</label>
                        <span>{currentUser.email_verified ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="profile-actions">
                {isOwnProfile ? (
                  <button 
                    onClick={() => setIsEditing(true)}
                    className="edit-profile-btn"
                  >
                    ✏️ Edit Profile
                  </button>
                ) : (
                  renderFriendshipButton()
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Share Profile Section */}
      {isOwnProfile && (
        <div className="share-profile-section">
          <h3>Share Your Profile</h3>
          <p>Share this link with others so they can view your profile and send friend requests:</p>
          <div className="share-link-container">
            <input 
              type="text" 
              value={window.location.href}
              readOnly
              className="share-link-input"
            />
            <button 
              onClick={handleCopyProfileLink}
              className="copy-link-btn"
            >
              📋 Copy Link
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicProfile; 